/*
  Cart upgrade upsell: the Upgrade button on snippets/cart-upgrade-offer.liquid.

  One click swaps the line's product A for product B at the same quantity.
  Order matters: add B first, then set A to 0. If the add fails (sold out, a
  network error) A stays in the cart and the card shows an error, so the
  shopper never ends up with neither.

  B is added with the private _upsell property. That property is how the admin
  and the ANCHOR order_paid Flow tell an upgraded line from a normal order of B.
  assets/custom.js rewrites line properties to add Total Entries; it now keeps
  the other properties, so _upsell survives that.

  The card is plain markup, not a form, and the button has no data-add-to-cart,
  so none of the theme's own add to cart handlers pick it up. The click is
  delegated from document because the theme replaces the cart HTML on every
  change. After the swap, theme:cart:refresh asks the theme to fetch and draw
  the cart again, which also redraws or removes this card.
*/

(function () {
  'use strict';

  var CARD_SELECTOR = '[data-cart-upgrade]';
  var BUTTON_SELECTOR = '[data-cart-upgrade-btn]';
  var ERROR_SELECTOR = '[data-cart-upgrade-error]';

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function postJson(path, body) {
    return fetch(routeRoot() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          throw new Error(data.description || data.message || 'Cart request failed');
        }
        return data;
      });
    });
  }

  /* id, quantity and properties[...] from the card's hidden inputs. */
  function readItem(card) {
    var item = { properties: {} };
    var inputs = card.querySelectorAll('input[type="hidden"][name]');

    for (var i = 0; i < inputs.length; i++) {
      var name = inputs[i].name;
      var value = inputs[i].value;
      var match = name.match(/^properties\[(.+)\]$/);

      if (match) {
        item.properties[match[1]] = value;
      } else if (name === 'id' || name === 'quantity') {
        item[name] = parseInt(value, 10);
      }
    }

    return item;
  }

  function getCart() {
    return fetch(routeRoot() + 'cart.js').then(function (response) {
      return response.json();
    });
  }

  /* { key: 0 } for the from line. The key changes whenever custom.js rewrites
     the line's properties, which it does after every cart redraw, so the key in
     the card's HTML is usually stale: then zero every line of the from variant.
     Keys, not the variant id: update.js by variant id only hits the first line. */
  function zeroFromLines(cart, fromKey, fromVariant) {
    var updates = {};
    cart.items.forEach(function (line) {
      if (line.key === fromKey) updates[line.key] = 0;
    });
    if (Object.keys(updates).length) return updates;

    cart.items.forEach(function (line) {
      if (String(line.variant_id) === String(fromVariant)) updates[line.key] = 0;
    });
    return updates;
  }

  /* cart is read at the same time as the add, so this is one request, not a
     failed change.js by the stale key plus two more. If a properties sync
     changed the key in between, read the cart again and retry once. */
  function removeFrom(cart, fromKey, fromVariant) {
    var updates = zeroFromLines(cart, fromKey, fromVariant);
    if (!Object.keys(updates).length) return Promise.resolve();

    return postJson('cart/update.js', { updates: updates })
      .then(function (after) {
        return Object.keys(zeroFromLines(after, fromKey, fromVariant)).length ? Promise.reject() : after;
      })
      .catch(function () {
        return getCart().then(function (fresh) {
          return postJson('cart/update.js', { updates: zeroFromLines(fresh, fromKey, fromVariant) });
        });
      });
  }

  /* custom.js syncs the entries lines after every cart redraw. */
  function refreshCart() {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  }

  /* is-loading on the button shows the theme's own spinner (.btn__loader).
     On success it keeps spinning until the cart redraw replaces the card. */
  function setBusy(card, button, busy) {
    card.classList.toggle('is-loading', busy);
    button.classList.toggle('is-loading', busy);
    button.disabled = busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /* While the swap runs, the cart lines take no taps. The theme changes lines
     by their position, and the new B line goes in at the top, so a + tapped
     on A in that moment landed on B. Unlocked once the redraw is in. */
  function lockLines(items, locked) {
    if (items) items.classList.toggle('is-upgrading', locked);
  }

  function unlockAfterRedraw(items) {
    var timer;
    function unlock() {
      clearTimeout(timer);
      document.removeEventListener('theme:cart:change', unlock);
      lockLines(items, false);
    }
    document.addEventListener('theme:cart:change', unlock);
    timer = setTimeout(unlock, 10000);
  }

  function showError(card, show) {
    var error = card.querySelector(ERROR_SELECTOR);
    if (error) error.hidden = !show;
  }

  function upgrade(card, button) {
    var item = readItem(card);
    if (!item.id || !item.quantity) return;

    var items = card.closest('cart-items');
    setBusy(card, button, true);
    lockLines(items, true);
    showError(card, false);

    /* The add does not touch the from line, so reading the cart alongside it
       is safe, and the from line is only removed once the add worked. */
    Promise.all([getCart(), postJson('cart/add.js', { items: [item] })])
      .then(function (results) {
        return removeFrom(results[0], card.dataset.fromKey, card.dataset.fromVariant);
      })
      .then(function () {
        unlockAfterRedraw(items);
        refreshCart();
      })
      .catch(function () {
        setBusy(card, button, false);
        lockLines(items, false);
        showError(card, true);
        refreshCart();
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return;

    var card = button.closest(CARD_SELECTOR);
    if (!card || button.disabled) return;

    event.preventDefault();
    upgrade(card, button);
  });
})();
